<template>
  <div class="planes">
    <div class="filtros">
      <div class="search">
        <PortalIcon name="Search" :size="15" style="color: var(--pt-muted)" />
        <input
          v-model="q"
          placeholder="Buscar folio, título, cliente o responsable…"
          class="search-inp"
        />
      </div>
      <select v-model="fArea" class="select" title="Filtrar por área">
        <option value="">Todas las áreas</option>
        <option v-for="a in areas" :key="a" :value="a">{{ a }}</option>
      </select>
      <select v-model="fEstatus" class="select" title="Filtrar por estatus">
        <option value="">Todos los estatus</option>
        <option v-for="e in estatusUsados" :key="e.cve" :value="e.cve">
          {{ e.label }}
        </option>
      </select>
      <button class="nuevo" @click="modal = true">
        <PortalIcon name="Plus" :size="16" /> Nuevo plan
      </button>
      <button class="recargar" :disabled="loading" @click="cargar">
        <PortalIcon name="RotateCcw" :size="14" /> Recargar
      </button>
    </div>

    <div v-if="loading" class="estado">Cargando planes…</div>
    <div v-else-if="error" class="estado error">{{ error }}</div>
    <PlanTable v-else :planes="filtrados" @abrir="abrir" />

    <NuevoPlanModal
      v-if="modal"
      :claims="claims"
      :busy="creando"
      :error="crearError"
      @close="cerrarModal"
      @crear="onCrear"
    />

    <PlanDetalle
      v-if="detalle"
      :plan="detalle"
      :sesion="sesion"
      @close="detalle = null"
      @actualizado="refrescarDetalle"
    />

    <AlertDialog
      v-if="alerta"
      :variant="alerta.variant"
      :title="alerta.title"
      :message="alerta.message"
      @close="alerta = null"
    />
  </div>
</template>

<script>
import PortalIcon from "./PortalIcon.vue";
import PlanTable from "./PlanTable.vue";
import PlanDetalle from "./PlanDetalle.vue";
import NuevoPlanModal from "./NuevoPlanModal.vue";
import AlertDialog from "./AlertDialog.vue";
import cxService from "../../../services/cx.service";
import { estatusLabel } from "./cx.rules";
import { normTxt } from "./portal.helpers";

export default {
  name: "PlanesTab",
  components: { PortalIcon, PlanTable, PlanDetalle, NuevoPlanModal, AlertDialog },
  props: {
    // Incidencias del portal, para poder crear un plan desde aquí.
    claims: { type: Array, default: () => [] },
    // Abre directamente un plan al montar (viene de RegistrosTab).
    abrirPlan: { type: [Number, String], default: null },
  },
  emits: ["creado"],
  data() {
    return {
      planes: [],
      loading: true,
      error: "",
      q: "",
      fArea: "",
      fEstatus: "",
      modal: false,
      creando: false,
      crearError: "",
      detalle: null,
      alerta: null,
      sesion: {},
    };
  },
  computed: {
    areas() {
      const s = new Set();
      this.planes.forEach((p) => p.area && s.add(p.area));
      return Array.from(s).sort((a, b) => a.localeCompare(b, "es"));
    },
    // Solo se ofrecen los estatus que realmente aparecen: un desplegable con
    // los 14 estados, la mayoría vacíos, no ayuda a filtrar.
    estatusUsados() {
      const s = new Set();
      this.planes.forEach((p) => p.estatus && s.add(p.estatus));
      return Array.from(s).map((cve) => ({ cve, label: estatusLabel(cve) }));
    },
    filtrados() {
      const q = normTxt(this.q);
      return this.planes.filter((p) => {
        if (this.fArea && p.area !== this.fArea) return false;
        if (this.fEstatus && p.estatus !== this.fEstatus) return false;
        if (!q) return true;
        return (
          normTxt(p.folio).includes(q) ||
          normTxt(p.folioClaim).includes(q) ||
          normTxt(p.titulo).includes(q) ||
          normTxt(p.cliente).includes(q) ||
          normTxt(p.responsable).includes(q)
        );
      });
    },
  },
  async mounted() {
    this.sesion = await cxService.sesion();
    await this.cargar();
    if (this.abrirPlan) {
      const p = this.planes.find((x) => String(x.idPlan) === String(this.abrirPlan));
      if (p) await this.abrir(p);
    }
  },
  methods: {
    async cargar() {
      this.loading = true;
      this.error = "";
      try {
        this.planes = await cxService.fetchPlanes();
      } catch (e) {
        this.error =
          "No se pudieron cargar los planes de acción. Revisa la conexión con el servicio.";
      } finally {
        this.loading = false;
      }
    },
    async abrir(p) {
      try {
        this.detalle = await cxService.fetchPlan(p.idPlan);
      } catch (e) {
        this.alerta = {
          variant: "error",
          title: "No se pudo abrir el plan",
          message: e.message,
        };
      }
    },
    // Tras cada acción del detalle se relee el plan y el listado: el backend
    // pudo haber cambiado el estatus y el avance por su cuenta.
    async refrescarDetalle() {
      if (!this.detalle) return;
      try {
        this.detalle = await cxService.fetchPlan(this.detalle.idPlan);
        await this.cargar();
      } catch (e) {
        this.alerta = {
          variant: "error",
          title: "No se pudo actualizar el plan",
          message: e.message,
        };
      }
    },
    cerrarModal() {
      this.modal = false;
      this.crearError = "";
    },
    async onCrear(form) {
      this.creando = true;
      this.crearError = "";
      try {
        const r = await cxService.crearPlan(form);
        this.modal = false;
        await this.cargar();
        this.$emit("creado", r);
        this.alerta = {
          variant: "success",
          title: "Plan creado",
          message:
            "Se creó el plan " +
            r.folio +
            " y se notificó al responsable. Queda pendiente de su aceptación.",
        };
      } catch (e) {
        this.crearError = e.message;
      } finally {
        this.creando = false;
      }
    },
  },
};
</script>

<style scoped>
.planes {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.filtros {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}
.search {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 8px;
  flex: 1;
  min-width: 200px;
  background: var(--pt-card);
  border: 1px solid var(--pt-border);
}
.search-inp {
  background: transparent;
  border: 0;
  outline: none;
  color: var(--pt-text);
  font-size: 14px;
  width: 100%;
  font-family: inherit;
}
.select {
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  background: var(--pt-card);
  color: var(--pt-text);
  border: 1px solid var(--pt-border);
  outline: none;
  font-family: inherit;
}
.nuevo {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #fff;
  background: #003ca5;
  border: 0;
  cursor: pointer;
  font-family: inherit;
}
.recargar {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  background: var(--pt-card);
  color: var(--pt-dim);
  border: 1px solid var(--pt-border);
  cursor: pointer;
  font-family: inherit;
}
.estado {
  text-align: center;
  font-size: 14px;
  color: var(--pt-muted);
  padding: 40px 0;
}
.estado.error {
  color: #b91c1c;
}
</style>
